import React, { useState } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions } from '@material-ui/core';

import { GQLParty, GQLSymbolAssetInput } from '@jewelutions/core-crm-server-api-client-domain';

import { ErrorComponent, JwlCheckbox, JwlField, LoadingSpinner } from '../../../../components';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';

interface IProps {
  party: GQLParty;
  dialogId: string;
  symbolCollectionId: string;
  onClose: (success: boolean) => void;
}

const initialValues = {
  title: '',
  description: '',
  public: true
};

export const SymbolCreateDialog: React.FC<IProps> = ({
  dialogId,
  party,
  symbolCollectionId,
  onClose
}: IProps) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [symbolToPersist, setSymbolToPersist] = useState(null as any);
  const { t: translate } = useTranslation();

  const [
    createSymbol,
    { error: errorCreateSymbol, loading: createSymbolLoader }
  ] = GQLOperationBuilder.symbol.createSymbol();

  const onSubmit = (props: any) => {
    const input: GQLSymbolAssetInput = {
      ownerPartyId: party.id,
      public: true,
      title: { en: props.title },
      description: { en: props.description }
    };

    const mutationVariables = { input: input, symbolCollectionId: symbolCollectionId };

    setSymbolToPersist(props);
    createSymbol({ variables: mutationVariables }).then(_ => {
      onClose(true);
    });
  };

  if (errorCreateSymbol && !errorComponentState)
    return (
      <Dialog open={true} maxWidth="md" aria-labelledby="add-font">
        <ErrorComponent
          margins="0"
          close={setErrorComponentState}
          callback={() => onSubmit(symbolToPersist)}
          details={errorCreateSymbol?.stack}
          message={errorCreateSymbol?.message}
        />
      </Dialog>
    );

  if (createSymbolLoader) return <LoadingSpinner />;

  const validationSchema = Yup.object().shape({
    description: Yup.string().required('Description is required'),
    title: Yup.string().required('Title is required')
  });

  return (
    <Dialog open={true} id={dialogId} maxWidth="md" aria-labelledby={dialogId}>
      <DialogTitle>Create symbol</DialogTitle>
      <DialogContent className="w-dialog-small">
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
          {({ values, touched, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Field name={`title`} component={JwlField} label="Title" />

              <Field name={`description`} component={JwlField} label="Description" />

              <Field name="public" component={JwlCheckbox} label="Public" />

              <DialogActions>
                <Button color="primary" onClick={() => onClose(false)}>
                  Close
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  Save
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default SymbolCreateDialog;
